import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui';
import CallInGhl from '../CallInGhl';
import ReplyPanel from './ReplyPanel';
import LaterMenu from '../todos/LaterMenu';
import { TodoFacts } from '../todos/TodoList';
import { NO_CONTACT, SHARED, agoLabel, dueDay, isOverdue, untilLabel, wordsOf } from '../todos/todoFormat';
import {
  api, type CardText, type LaterWhen, type MorningCall, type MorningDeadline, type MorningMailItem, type MorningPromise, type MorningRef,
  type MorningText, type MorningTodo, type TodoItem,
} from '../../lib/api';

/**
 * The Morning page's rows, in two looks.
 *  - TODAY'S ROWS (MORNING1, moved here unchanged from `pages/Morning.tsx`): the type sections Robert sees, and Joseph
 *    until he is switched on. ⛔ No kind tag - those sections render exactly as before (Morning.test.tsx, unchanged).
 *  - THE CARDS (MORNING2 Task 9, his Duet pick 2026-10-28): the grouped view inside the morning chat. Each card carries
 *    its kind tag (Email / Text / Missed call / To-do / Deadline), a one-line title, ONE plain line from the item's own
 *    fields (no AI), then pill buttons - the primary one dark. "Start" only where a chat thread begins (an email; a to-do
 *    whose contact's email is waiting), and every Start is off while an answer is arriving.
 * Phone first: everything wraps at 375 px and nothing widens the column.
 */

export const NOT_SAVED = 'Not saved - try again.';
export const NO_NAME = 'No name in GoHighLevel';

export const H2 = 'mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500';
export const ROW = 'min-w-0 rounded-lg border border-gray-200 bg-white p-4';
export const ACTIONS = 'mt-3 flex flex-wrap items-center gap-2';
export const LINK = 'text-sm font-medium text-primary-600 hover:underline';

/** One section: its heading, then its rows - or the server's words when it could not be read. */
export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="mb-8">
      <h2 className={H2}>{title}</h2>
      {children}
    </section>
  );
}

/** ⛔ An unreadable source says so, with a retry - never "nothing waiting". */
export function Unreadable({ words, onRetry, busy }: { words: string; onRetry: () => void; busy: boolean }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <span className="min-w-0 break-words">{words}</span>
      <Button size="sm" variant="outline" onClick={onRetry} isLoading={busy}>Try again</Button>
    </div>
  );
}

/** Who, and the R9 badge when nobody owns the conversation in GoHighLevel. */
export function Who({ name, shared }: { name: string | null; shared: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="min-w-0 break-words text-sm font-semibold text-gray-900">{name ?? NO_NAME}</span>
      {shared && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{SHARED}</span>}
    </div>
  );
}

/**
 * A to-do's Done / Undo / Later, as on the To-dos page - shared by today's row and the card.
 * ⛔ It changes only after the server says yes; a refusal stays, with the server's words.
 */
function useTodoTaps(id: string) {
  const [closed, setClosed] = useState(false);
  const [until, setUntil] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (write: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await write();
    } catch (e) {
      setError(wordsOf(e, NOT_SAVED));
    } finally {
      setBusy(false);
    }
  };
  return {
    closed, until, busy, error,
    done: () => void run(async () => { await api.todos.done(id); setClosed(true); }),
    reopen: () => void run(async () => { await api.todos.reopen(id); setClosed(false); }),
    later: (when: LaterWhen) => void run(async () => { setUntil((await api.todos.later(id, when)).until); }),
  };
}

/** "Marked done. Undo" - until the next read, and no longer. */
function MarkedDone({ taps }: { taps: ReturnType<typeof useTodoTaps> }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
      <span>Marked done.</span>
      <Button size="sm" variant="ghost" disabled={taps.busy} onClick={taps.reopen}>Undo</Button>
      {taps.error && <span role="alert" className="text-red-700">{taps.error}</span>}
    </li>
  );
}

/** A to-do due today or overdue: TODO1's facts and its Done / Later, as on the To-dos page. */
export function TodoRow({ todo }: { todo: TodoItem }) {
  const taps = useTodoTaps(todo.ghl_task_id);
  if (taps.closed) return <MarkedDone taps={taps} />;
  return (
    <li className={ROW}>
      <TodoFacts todo={todo} title={<p className="mt-1 break-words text-[15px] font-medium text-gray-900">{todo.title}</p>}>
        {taps.until && <span className="ml-2">{untilLabel(taps.until)}</span>}
      </TodoFacts>
      <div className={ACTIONS}>
        {todo.ghl_contact_url && <CallInGhl url={todo.ghl_contact_url} />}
        {todo.ghl_contact_id !== null ? (
          <Button size="sm" disabled={taps.busy} onClick={taps.done}>Done</Button>
        ) : (
          <span className="text-xs text-gray-600">{NO_CONTACT}</span>
        )}
        <LaterMenu disabled={taps.busy} onPick={taps.later} />
      </div>
      {taps.error && <p role="alert" className="mt-2 text-sm text-red-700">{taps.error}</p>}
    </li>
  );
}

/** ⭐ A DATE, not an instant: read at noon UTC, so Pacific never moves it to the day before. */
const deadlineDay = (d: MorningDeadline) => dueDay(`${d.due_date}T12:00:00.000Z`);

export function DeadlineRow({ d }: { d: MorningDeadline }) {
  const day = deadlineDay(d);
  return (
    <li>
      <Link to={`/crm/deals/${encodeURIComponent(d.deal_id)}`} className={`${ROW} block hover:border-gray-400`}>
        <span className="block break-words text-sm font-semibold text-gray-900">{d.deal_name ?? 'Unnamed deal'}</span>
        <span className="mt-1 block break-words text-[15px] font-medium text-gray-900">{d.label}</span>
        <span className="mt-1 block text-xs text-gray-500">
          <span>{`Due ${day}`}</span>
          {d.overdue && <span className="ml-2 font-semibold text-red-700">Overdue</span>}
        </span>
      </Link>
    </li>
  );
}

/** "Not needed" on one of today's rows: its tap's state, and the tap. */
export interface Dismiss { busy: boolean; error: string | null; onDismiss: () => void }

function NotNeeded({ dismiss }: { dismiss: Dismiss }) {
  return <Button size="sm" variant="outline" disabled={dismiss.busy} onClick={dismiss.onDismiss}>Not needed</Button>;
}
function NotNeededError({ dismiss }: { dismiss: Dismiss }) {
  return dismiss.error ? <p role="alert" className="mt-2 text-sm text-red-700">{dismiss.error}</p> : null;
}

export function TextRow({ t, dismiss }: { t: MorningText; dismiss: Dismiss }) {
  return (
    <li className={ROW}>
      <Who name={t.contact_name} shared={t.shared} />
      {t.last_message_body && <p className="mt-1 line-clamp-3 whitespace-pre-wrap break-words text-[15px] text-gray-900">{t.last_message_body}</p>}
      <p className="mt-1 text-xs text-gray-500">{`Texted ${agoLabel(t.last_message_date)}`}</p>
      <div className={ACTIONS}>
        {t.ghl_contact_url && <CallInGhl url={t.ghl_contact_url} />}
        {/* The contact's thread, where the draft waits in the box - ⛔ never sent by itself. */}
        {t.contact_id && (
          <Link to={`/crm/conversations?${new URLSearchParams({ contact: t.contact_id })}`} className={LINK}>Draft reply</Link>
        )}
        <NotNeeded dismiss={dismiss} />
      </div>
      <NotNeededError dismiss={dismiss} />
    </li>
  );
}

export function MailRow({ t, sent, replying, onReply, onCloseReply, onSent, dismiss }: {
  t: MorningMailItem; sent: boolean; replying: boolean;
  onReply: () => void; onCloseReply: () => void; onSent: () => void; dismiss: Dismiss;
}) {
  if (sent) {
    return (
      <li className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <span className="block min-w-0 break-words text-sm text-gray-700">{t.subject}</span>
        <p className="mt-1 text-sm font-semibold text-green-700">Sent</p>
      </li>
    );
  }
  return (
    <li className={ROW}>
      <span className="block min-w-0 break-words text-sm font-semibold text-gray-900">{t.from}</span>
      <p className="mt-1 break-words text-[15px] font-medium text-gray-900">{t.subject}</p>
      <p className="mt-1 text-xs text-gray-500">{`Emailed ${agoLabel(t.at)}`}</p>
      {t.attachment_names.length > 0 && (
        <p className="mt-1 break-words text-xs text-gray-600">{`Attached: ${t.attachment_names.join(', ')}`}</p>
      )}
      <div className={ACTIONS}>
        {/* ⛔ Reply only opens a draft; sending is the panel's "Send to ...?" dialog. */}
        {!replying && <Button size="sm" onClick={onReply}>Reply</Button>}
        <a href={t.gmail_url} target="_blank" rel="noopener noreferrer" className={LINK}>Open in Gmail</a>
        <NotNeeded dismiss={dismiss} />
      </div>
      <NotNeededError dismiss={dismiss} />
      {replying && <ReplyPanel threadId={t.thread_id} onClose={onCloseReply} onSent={onSent} />}
    </li>
  );
}

export function CallRow({ c, dismiss }: { c: MorningCall; dismiss: Dismiss }) {
  return (
    <li className={ROW}>
      <Who name={c.contact_name} shared={c.shared} />
      <p className="mt-1 text-xs text-gray-500">{`Missed call ${agoLabel(c.at)}`}</p>
      <div className={ACTIONS}>
        {c.ghl_contact_url && <CallInGhl url={c.ghl_contact_url} />}
        <NotNeeded dismiss={dismiss} />
      </div>
      <NotNeededError dismiss={dismiss} />
    </li>
  );
}

// ── The cards (MORNING2, the grouped view inside the morning chat) ──

/** The tag on every card: what kind of thing is waiting. */
export const KIND_LABEL: Record<MorningRef['kind'], string> = {
  mail: 'Email', text: 'Text', call: 'Missed call', todo: 'Task', deadline: 'Deadline', promise: 'Promise',
};

export function KindTag({ kind }: { kind: MorningRef['kind'] }) {
  return (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
      {KIND_LABEL[kind]}
    </span>
  );
}

// CHAT-DUET (2026-10-28): the cards in the Duet look - 15px pills (Start the round black, the rest outlined), a 15px line.
const PILL = 'inline-flex max-w-full items-center justify-center rounded-full px-4 py-1.5 text-left text-[15px] leading-snug break-words transition-colors disabled:cursor-not-allowed disabled:opacity-40';
export const PRIMARY = `${PILL} bg-chat-black font-semibold text-white hover:bg-gray-800`;
export const OUTLINE = `${PILL} border border-gray-300 bg-white font-medium text-gray-800 hover:border-gray-900`;
const LINE = 'mt-0.5 min-w-0 break-words text-[15px] leading-[20px] text-gray-600';
const SHARED_BADGE = 'rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600';

/**
 * "Why?" on an AI card: the INPUT lines the reason came from, as the server copied them from what the model was given.
 * ⛔ Never words the model wrote. Closed until he taps; a card with no AI text has no Why?.
 */
function Why({ sources }: { sources: CardText['sources'] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1">
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        className="-ml-1 rounded px-1 py-1.5 text-[13px] font-medium text-gray-500 hover:text-gray-900 hover:underline">
        Why?
      </button>
      {open && (
        <ul className="mt-1 space-y-1 rounded-xl bg-gray-50 px-3 py-2 text-[13px] leading-[18px] text-gray-600">
          {sources.map((s) => <li key={s.id} className="break-words">{s.text}</li>)}
        </ul>
      )}
    </div>
  );
}

/**
 * The card: tag, title, one line, then its buttons - and a refusal's words under them.
 * MORNING2-AI: with `ai` (a checked title and reason) the heading is `ai.title`, the line is the card's own (it passes
 * `ai.reason`) and a Why? shows where the reason came from; without it, exactly phase 1's title and plain line.
 */
function Card({ kind, title, shared, line, extra, error, ai, children }: {
  kind: MorningRef['kind']; title: string; shared?: boolean; line: ReactNode; extra?: ReactNode; error?: string | null;
  ai?: CardText; children: ReactNode;
}) {
  const heading = ai?.title ?? title;
  return (
    <article aria-label={heading} className="min-w-0 rounded-[20px] bg-white p-4 shadow-chat-card">
      <div className="flex flex-wrap items-center gap-2">
        <KindTag kind={kind} />
        {shared && <span className={SHARED_BADGE}>{SHARED}</span>}
      </div>
      <h3 className="mt-1.5 min-w-0 break-words text-[17px] font-semibold leading-[23px] tracking-[-0.011em] text-chat-ink">{heading}</h3>
      {line}
      {extra}
      {ai && <Why sources={ai.sources} />}
      <div className="mt-3 flex flex-wrap items-center gap-2">{children}</div>
      {error && <p role="alert" className="mt-2 break-words text-sm text-red-700">{error}</p>}
    </article>
  );
}

/**
 * Call on a card - today's link: GoHighLevel's dialer places the call. A real link, ready before the tap (an iPhone drops
 * a window opened after an await). The missed call's main button; an outline one on a text and a to-do (spec 4.2: every
 * item keeps today's Call).
 */
function CallLink({ url, className }: { url: string; className: string }) {
  return <a href={url} target="_blank" rel="noreferrer" title="Call (opens GoHighLevel)" className={className}>Call</a>;
}

/** Hide on a card: today's "Not needed", and the words when the server refused it. */
export interface Hide { onHide: () => void; error: string | null }

/** The words of the turn a Start sends - the same as if he had typed them. */
export const draftReplyWords = (t: MorningMailItem) => `Draft a reply to ${t.from} - ${t.subject}`;

/**
 * An email waiting on him. Start = Draft reply: a chat turn through the panel's own send (⛔ never a send of mail).
 * Hide = today's Not needed. Open in Gmail as today.
 */
export function MailCard({ t, onStart, answering, hide, ai }: {
  t: MorningMailItem; onStart: () => void; answering: boolean; hide: Hide; ai?: CardText;
}) {
  return (
    <Card
      kind="mail"
      title={`Reply to ${t.from}`}
      ai={ai}
      line={<p className={LINE}>{ai ? ai.reason : `${t.subject} - ${agoLabel(t.at)}`}</p>}
      extra={t.attachment_names.length > 0
        ? <p className="mt-0.5 break-words text-xs text-gray-500">{`Attached: ${t.attachment_names.join(', ')}`}</p>
        : null}
      error={hide.error}
    >
      <button type="button" className={PRIMARY} disabled={answering} onClick={onStart}>Start</button>
      <button type="button" className={OUTLINE} onClick={hide.onHide}>Hide</button>
      <a href={t.gmail_url} target="_blank" rel="noopener noreferrer" className={OUTLINE}>Open in Gmail</a>
    </Card>
  );
}

/** A text waiting on him: the last message on ONE line (cut with an ellipsis), then how long ago. Texts get their own
 *  card in phase 3; until then Open in Conversations is today's link. */
export function TextCard({ t, hide, ai }: { t: MorningText; hide: Hide; ai?: CardText }) {
  const ago = agoLabel(t.last_message_date);
  const to = t.contact_id ? `/crm/conversations?${new URLSearchParams({ contact: t.contact_id })}` : '/crm/conversations';
  return (
    <Card
      kind="text"
      title={t.contact_name ? `Text from ${t.contact_name}` : `Text - ${NO_NAME}`}
      shared={t.shared}
      ai={ai}
      line={ai ? <p className={LINE}>{ai.reason}</p> : t.last_message_body ? (
        <p className={`${LINE} flex`}>
          <span className="min-w-0 truncate">{t.last_message_body}</span>
          <span className="shrink-0 whitespace-pre">{` - ${ago}`}</span>
        </p>
      ) : <p className={LINE}>{ago}</p>}
      error={hide.error}
    >
      <Link to={to} className={PRIMARY}>Open in Conversations</Link>
      {t.ghl_contact_url && <CallLink url={t.ghl_contact_url} className={OUTLINE} />}
      <button type="button" className={OUTLINE} onClick={hide.onHide}>Hide</button>
    </Card>
  );
}

export function CallCard({ c, hide, ai }: { c: MorningCall; hide: Hide; ai?: CardText }) {
  return (
    <Card
      kind="call"
      title={c.contact_name ? `Missed call from ${c.contact_name}` : `Missed call - ${NO_NAME}`}
      shared={c.shared}
      ai={ai}
      line={<p className={LINE}>{ai ? ai.reason : agoLabel(c.at)}</p>}
      error={hide.error}
    >
      {c.ghl_contact_url && <CallLink url={c.ghl_contact_url} className={PRIMARY} />}
      <button type="button" className={OUTLINE} onClick={hide.onHide}>Hide</button>
    </Card>
  );
}

/**
 * A to-do due today or overdue. With its contact's email waiting (`mail`), Start drafts that reply - carrying this to-do,
 * so the send ticks it off - then Done, Later; without, Done first, then Later.
 */
export function TodoCard({ todo, mail, onStart, answering, ai }: {
  todo: MorningTodo; mail: MorningMailItem | null; onStart: () => void; answering: boolean; ai?: CardText;
}) {
  const taps = useTodoTaps(todo.ghl_task_id);
  if (taps.closed) return <MarkedDone taps={taps} />;
  const now = new Date();
  const due = !todo.due_at ? 'No due date' : isOverdue(todo.due_at, now) ? 'Overdue' : `Due ${dueDay(todo.due_at, now)}`;
  const line = [todo.contact_name, due].filter(Boolean).join(' - ');
  return (
    <li>
      <Card
        kind="todo"
        title={todo.title}
        shared={todo.shared}
        ai={ai}
        // Later's answer ("until ...") stays beside the reason: it is what his tap did, not something the model said.
        line={<p className={LINE}>{ai ? ai.reason : line}{taps.until && <span className="ml-2">{untilLabel(taps.until)}</span>}</p>}
        error={taps.error}
      >
        {mail && <button type="button" className={PRIMARY} disabled={answering} onClick={onStart}>Start</button>}
        {todo.ghl_contact_id !== null ? (
          <button type="button" className={mail ? OUTLINE : PRIMARY} disabled={taps.busy} onClick={taps.done}>Done</button>
        ) : (
          <span className="text-xs text-gray-600">{NO_CONTACT}</span>
        )}
        {todo.ghl_contact_url && <CallLink url={todo.ghl_contact_url} className={OUTLINE} />}
        <LaterMenu disabled={taps.busy} onPick={taps.later} triggerClassName={OUTLINE} />
      </Card>
    </li>
  );
}

export function DeadlineCard({ d, ai }: { d: MorningDeadline; ai?: CardText }) {
  return (
    <Card
      kind="deadline"
      title={d.label}
      ai={ai}
      line={(
        <p className={LINE}>
          {ai ? ai.reason : `${d.deal_name ?? 'Unnamed deal'} - due ${deadlineDay(d)}`}
          {d.overdue && <span className="ml-1.5 font-semibold text-red-700">Overdue</span>}
        </p>
      )}
    >
      <Link to={`/crm/deals/${encodeURIComponent(d.deal_id)}`} className={PRIMARY}>Open deal</Link>
    </Card>
  );
}

/** The words of the turn a promise card's Draft reply sends - the same shape as a mail card's. */
export const draftPromiseWords = (p: MorningPromise) => `Draft a reply to ${p.to} - ${p.subject}`;

/**
 * A promise he made in his sent mail (design 4.3). Plain: "You promised: <his sentence>", then who it went to, the subject
 * and when he sent it. Draft reply = phase 1's Start for that thread (a chat turn, ⛔ never a send of mail); Hide = "Not needed"
 * for the promise; Open in Gmail opens the thread.
 */
export function PromiseCard({ p, onStart, answering, hide, ai }: {
  p: MorningPromise; onStart: () => void; answering: boolean; hide: Hide; ai?: CardText;
}) {
  return (
    <Card
      kind="promise"
      title={`You promised: ${p.sentence}`}
      ai={ai}
      line={<p className={LINE}>{ai ? ai.reason : `To ${p.to} - ${p.subject} - sent ${agoLabel(p.sent_at)}`}</p>}
      error={hide.error}
    >
      <button type="button" className={PRIMARY} disabled={answering} onClick={onStart}>Draft reply</button>
      <button type="button" className={OUTLINE} onClick={hide.onHide}>Hide</button>
      <a href={p.gmail_url} target="_blank" rel="noopener noreferrer" className={OUTLINE}>Open in Gmail</a>
    </Card>
  );
}

// ── One card per contact (MORNING2-AI Task 8, design 4.2) ──

/** What one item of a group needs to draw itself: its row, and (for an email or a task) the Start it was given. */
export type GroupItem =
  | { kind: 'mail'; t: MorningMailItem; onStart: () => void }
  | { kind: 'text'; t: MorningText }
  | { kind: 'call'; c: MorningCall }
  | { kind: 'todo'; todo: MorningTodo; mail: MorningMailItem | null; onStart: () => void };

const itemKey = (item: GroupItem): string => {
  switch (item.kind) {
    case 'mail': return `mail:${item.t.thread_id}`;
    case 'text': return `text:${item.t.conversation_id}`;
    case 'call': return `call:${item.c.conversation_id}`;
    case 'todo': return `todo:${item.todo.ghl_task_id}`;
  }
};

/** The person's name as one of the group's items has it (an email's sender, a text's contact ...), or null. */
const nameInGroup = (items: GroupItem[]): string | null => {
  for (const item of items) {
    const name = item.kind === 'mail' ? item.t.from : item.kind === 'text' ? item.t.contact_name
      : item.kind === 'call' ? item.c.contact_name : item.todo.contact_name;
    if (name && name.trim()) return name.trim();
  }
  return null;
};

const KIND_ONE: Record<GroupItem['kind'], string> = { mail: 'an email', text: 'a text', call: 'a missed call', todo: 'a task' };
const KIND_MANY: Record<GroupItem['kind'], string> = { mail: 'emails', text: 'texts', call: 'missed calls', todo: 'tasks' };

/** What a group holds, in words, for the plain line: "An email and a text", "2 texts, a missed call and a task". */
export function groupContents(items: GroupItem[]): string {
  const counts = new Map<GroupItem['kind'], number>();
  for (const item of items) counts.set(item.kind, (counts.get(item.kind) ?? 0) + 1);
  const parts = [...counts].map(([kind, n]) => (n === 1 ? KIND_ONE[kind] : `${n} ${KIND_MANY[kind]}`));
  const words = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0] ?? '';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** One item under a group's heading: its kind tag, its own words, and its own buttons. */
function GroupLine({ kind, shared, title, line, children }: {
  kind: MorningRef['kind']; shared?: boolean; title?: ReactNode; line?: ReactNode; children: ReactNode;
}) {
  return (
    <li className="min-w-0 border-t border-gray-100 pt-3 first:border-t-0 first:pt-0">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <KindTag kind={kind} />
        {shared && <span className={SHARED_BADGE}>{SHARED}</span>}
      </div>
      {title}
      {line}
      <div className="mt-2 flex flex-wrap items-center gap-2">{children}</div>
    </li>
  );
}

const GROUP_TITLE = 'mt-1 min-w-0 break-words text-[15px] font-medium leading-[20px] text-chat-ink';
const GROUP_LINE = 'mt-0.5 min-w-0 break-words text-[13px] leading-[18px] text-gray-500';

/** A task inside a group: its own Done / Later, as on its own card ("Marked done. Undo" takes its place until the next read). */
function GroupTodoLine({ todo, mail, onStart, answering }: { todo: MorningTodo; mail: MorningMailItem | null; onStart: () => void; answering: boolean }) {
  const taps = useTodoTaps(todo.ghl_task_id);
  if (taps.closed) return <MarkedDone taps={taps} />;
  const now = new Date();
  const due = !todo.due_at ? 'No due date' : isOverdue(todo.due_at, now) ? 'Overdue' : `Due ${dueDay(todo.due_at, now)}`;
  return (
    <GroupLine
      kind="todo"
      shared={todo.shared}
      title={<p className={GROUP_TITLE}>{todo.title}</p>}
      line={<p className={GROUP_LINE}>{[due, taps.until ? untilLabel(taps.until) : null].filter(Boolean).join(' - ')}</p>}
    >
      {mail && <button type="button" className={OUTLINE} disabled={answering} onClick={onStart}>Start</button>}
      {todo.ghl_contact_id !== null ? (
        <button type="button" className={OUTLINE} disabled={taps.busy} onClick={taps.done}>Done</button>
      ) : (
        <span className="text-xs text-gray-600">{NO_CONTACT}</span>
      )}
      {todo.ghl_contact_url && <CallLink url={todo.ghl_contact_url} className={OUTLINE} />}
      <LaterMenu disabled={taps.busy} onPick={taps.later} triggerClassName={OUTLINE} />
      {taps.error && <p role="alert" className="w-full break-words text-sm text-red-700">{taps.error}</p>}
    </GroupLine>
  );
}

/**
 * ONE card for everything one contact has waiting (design 4.2): the heading and one line (the AI's when it has written them,
 * else the person's name and what the group holds), then each item under it with ITS OWN buttons, and one Hide for the card.
 * `hide` is null when nothing in the group can be hidden in phase 1 (tasks only): then there is no Hide. The Hide is "Not needed"
 * for each item in the group that can be dismissed - the caller makes one call per item - and a refusal's words show here.
 * `epoch` is the page's read count: the rows remount on a new read, so "Marked done. Undo" lasts until the next read and no longer.
 */
export function GroupCard({ items, ai, hide, answering, epoch }: {
  items: GroupItem[]; ai?: CardText; hide: Hide | null; answering: boolean; epoch: number;
}) {
  const heading = ai?.title ?? `${nameInGroup(items) ?? NO_NAME} - ${items.length} things waiting`;
  return (
    <article aria-label={heading} className="min-w-0 rounded-[20px] bg-white p-4 shadow-chat-card">
      <h3 className="min-w-0 break-words text-[17px] font-semibold leading-[23px] tracking-[-0.011em] text-chat-ink">{heading}</h3>
      <p className={LINE}>{ai ? ai.reason : groupContents(items)}</p>
      {ai && <Why sources={ai.sources} />}
      <ul className="mt-3 space-y-3">
        {items.map((item) => {
          const key = `${epoch}:${itemKey(item)}`;
          switch (item.kind) {
            case 'mail':
              return (
                <GroupLine key={key} kind="mail" title={<p className={GROUP_TITLE}>{item.t.subject}</p>}
                  line={(
                    <p className={GROUP_LINE}>
                      {agoLabel(item.t.at)}
                      {item.t.attachment_names.length > 0 && ` - Attached: ${item.t.attachment_names.join(', ')}`}
                    </p>
                  )}>
                  <button type="button" className={OUTLINE} disabled={answering} onClick={item.onStart}>Start</button>
                  <a href={item.t.gmail_url} target="_blank" rel="noopener noreferrer" className={OUTLINE}>Open in Gmail</a>
                </GroupLine>
              );
            case 'text': {
              const to = item.t.contact_id ? `/crm/conversations?${new URLSearchParams({ contact: item.t.contact_id })}` : '/crm/conversations';
              return (
                <GroupLine key={key} kind="text" shared={item.t.shared}
                  title={item.t.last_message_body ? <p className={`${GROUP_TITLE} line-clamp-2 whitespace-pre-wrap`}>{item.t.last_message_body}</p> : null}
                  line={<p className={GROUP_LINE}>{agoLabel(item.t.last_message_date)}</p>}>
                  <Link to={to} className={OUTLINE}>Open in Conversations</Link>
                  {item.t.ghl_contact_url && <CallLink url={item.t.ghl_contact_url} className={OUTLINE} />}
                </GroupLine>
              );
            }
            case 'call':
              return (
                <GroupLine key={key} kind="call" shared={item.c.shared} line={<p className={GROUP_LINE}>{agoLabel(item.c.at)}</p>}>
                  {item.c.ghl_contact_url && <CallLink url={item.c.ghl_contact_url} className={OUTLINE} />}
                </GroupLine>
              );
            case 'todo':
              return <GroupTodoLine key={key} todo={item.todo} mail={item.mail} onStart={item.onStart} answering={answering} />;
          }
        })}
      </ul>
      {hide && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button" className={OUTLINE} onClick={hide.onHide}>Hide</button>
        </div>
      )}
      {hide?.error && <p role="alert" className="mt-2 break-words text-sm text-red-700">{hide.error}</p>}
    </article>
  );
}
